export const parseAlarmMetaData = (value: unknown): unknown => {
  if (!value) return null;
  if (typeof value === 'object') return value;
  if (typeof value !== 'string') return null;

  try {
    const normalizedJson = value
      .replace(/\bNaN\b/g, 'null')
      .replace(/\bInfinity\b/g, 'null')
      .replace(/\b-Infinity\b/g, 'null');

    return JSON.parse(normalizedJson);
  } catch {
    return null;
  }
};

export type AlarmImpact = {
  monthlyQuantityDelta: number | null;
  monthlyCost: number | null;
  unit: string;
  priceSource: string | null;
};

type ImpactMetaData = {
  impact_summary?: {
    total_monthly_quantity_delta?: unknown;
    total_monthly_cost?: unknown;
    unit?: unknown;
    price_source?: unknown;
  };
  alarm_details?: Array<{
    alarm_result?: {
      monthly_quantity_delta?: unknown;
      monthly_cost?: unknown;
    };
  }>;
};

const toNumber = (value: unknown) =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

export const getAlarmImpact = (metaDataValue: unknown): AlarmImpact | null => {
  const metaData = parseAlarmMetaData(metaDataValue) as ImpactMetaData | null;
  if (!metaData || typeof metaData !== 'object') return null;

  const summary = metaData.impact_summary;
  const firstResult = metaData.alarm_details?.find(
    (detail) => detail?.alarm_result,
  )?.alarm_result;

  const monthlyQuantityDelta =
    toNumber(summary?.total_monthly_quantity_delta) ??
    toNumber(firstResult?.monthly_quantity_delta);
  const monthlyCost =
    toNumber(summary?.total_monthly_cost) ??
    toNumber(firstResult?.monthly_cost);

  if (monthlyQuantityDelta === null && monthlyCost === null) return null;

  return {
    monthlyQuantityDelta,
    monthlyCost,
    unit: typeof summary?.unit === 'string' ? summary.unit : 'kWh',
    priceSource:
      typeof summary?.price_source === 'string' ? summary.price_source : null,
  };
};

export const formatNumber = (value: number) =>
  new Intl.NumberFormat('da-DK', { maximumFractionDigits: 0 }).format(value);

export const formatQuantity = (value: number, unit: string) =>
  `${new Intl.NumberFormat('da-DK', { maximumFractionDigits: 0 }).format(value)} ${unit}`;

export const formatCost = (value: number) =>
  new Intl.NumberFormat('da-DK', {
    style: 'currency',
    currency: 'DKK',
    maximumFractionDigits: 0,
  }).format(value);
